import type {
  CutDirection,
  TargetAppearance,
  TargetSize,
  TargetSpeed,
} from "./beatmap";
import type {
  GameMode,
  Grade,
  ObstacleRunEvent,
  TargetRunEvent,
} from "./useGameStore";

export interface BucketStat {
  label: string;
  total: number;
  hits: number;
  misses: number;
  points: number;
  hitRate: number;
  averagePoints: number;
}

export interface RunPerformanceStats {
  totalTargets: number;
  hitTargets: number;
  missedTargets: number;
  wrongDirectionTargets: number;
  hitRate: number;
  accuracyLabel: string;
  totalPointsFromTargets: number;
  averagePointsPerHit: number;
  tenouchiHits: number;
  obstacleTotal: number;
  obstacleCleared: number;
  obstacleHit: number;
  bestCuts: TargetRunEvent[];
  weakestCuts: TargetRunEvent[];
  recentMistakes: Array<TargetRunEvent | ObstacleRunEvent>;
  byDirection: BucketStat[];
  byHand: BucketStat[];
  bySize: BucketStat[];
  bySpeed: BucketStat[];
  byGrade: Record<Grade, number>;
  bestCategory: BucketStat | null;
  weakestCategory: BucketStat | null;
}

const GRADE_ORDER: Grade[] = [
  "IPPON",
  "PERFECT",
  "GREAT",
  "GOOD",
  "LATE",
  "MISS",
];
const DIRECTION_ORDER: CutDirection[] = [
  "any",
  "up",
  "down",
  "left",
  "right",
  "up-left",
  "up-right",
  "down-left",
  "down-right",
];
const SIZE_ORDER: TargetSize[] = ["large", "medium", "small"];
const SPEED_ORDER: TargetSpeed[] = ["slow", "base", "fast"];
const APPEARANCE_ORDER: TargetAppearance[] = ["standard", "fast"];

function emptyGradeCounts(): Record<Grade, number> {
  return { IPPON: 0, PERFECT: 0, GREAT: 0, GOOD: 0, LATE: 0, MISS: 0 };
}

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function makeBuckets<T extends string>(
  events: TargetRunEvent[],
  order: readonly T[],
  getKey: (event: TargetRunEvent) => T,
  getLabel: (key: T) => string = (key) => key,
): BucketStat[] {
  return order
    .map((key) => {
      const bucketEvents = events.filter((event) => getKey(event) === key);
      const hits = bucketEvents.filter((event) => event.result === "hit");
      const points = hits.reduce((sum, event) => sum + event.points, 0);
      return {
        label: getLabel(key),
        total: bucketEvents.length,
        hits: hits.length,
        misses: bucketEvents.length - hits.length,
        points,
        hitRate:
          bucketEvents.length > 0 ? hits.length / bucketEvents.length : 0,
        averagePoints: hits.length > 0 ? Math.round(points / hits.length) : 0,
      };
    })
    .filter((bucket) => bucket.total > 0);
}

function rankCategories(buckets: BucketStat[]): {
  bestCategory: BucketStat | null;
  weakestCategory: BucketStat | null;
} {
  const eligible = buckets.filter((bucket) => bucket.total >= 3);
  if (eligible.length === 0) {
    return { bestCategory: null, weakestCategory: null };
  }

  const ranked = eligible.slice().sort((a, b) => {
    const rateDiff = b.hitRate - a.hitRate;
    if (Math.abs(rateDiff) > 0.001) return rateDiff;
    return b.averagePoints - a.averagePoints;
  });

  return {
    bestCategory: ranked[0],
    weakestCategory: ranked[ranked.length - 1],
  };
}

export function getPerformanceStats(
  targetEvents: TargetRunEvent[],
  obstacleEvents: ObstacleRunEvent[],
): RunPerformanceStats {
  const hitEvents = targetEvents.filter((event) => event.result === "hit");
  const missedTargets = targetEvents.filter(
    (event) => event.result === "missed",
  ).length;
  const wrongDirectionTargets = targetEvents.filter(
    (event) => event.result === "wrong-direction",
  ).length;
  const totalPointsFromTargets = hitEvents.reduce(
    (sum, event) => sum + event.points,
    0,
  );
  const byGrade = emptyGradeCounts();

  for (const event of targetEvents) {
    byGrade[event.grade] += 1;
  }

  const byDirection = makeBuckets(
    targetEvents,
    DIRECTION_ORDER,
    (event) => event.dir,
  );
  const byHand = makeBuckets(
    targetEvents,
    ["left", "right", "any"],
    (event) => event.hand,
  );
  const bySize = makeBuckets(targetEvents, SIZE_ORDER, (event) => event.size);
  const bySpeed = makeBuckets(
    targetEvents,
    SPEED_ORDER,
    (event) => event.speed,
  );
  const byAppearance = makeBuckets(
    targetEvents,
    APPEARANCE_ORDER,
    (event) => event.appearance,
  );
  const { bestCategory, weakestCategory } = rankCategories([
    ...byDirection,
    ...byHand,
    ...bySize,
    ...bySpeed,
    ...byAppearance,
  ]);

  const recentTargetMistakes = targetEvents.filter(
    (event) => event.result !== "hit",
  );
  const obstacleMistakes = obstacleEvents.filter(
    (event) => event.result === "hit",
  );
  const recentMistakes = [...recentTargetMistakes, ...obstacleMistakes]
    .sort((a, b) => b.t - a.t)
    .slice(0, 8);

  return {
    totalTargets: targetEvents.length,
    hitTargets: hitEvents.length,
    missedTargets,
    wrongDirectionTargets,
    hitRate:
      targetEvents.length > 0 ? hitEvents.length / targetEvents.length : 0,
    accuracyLabel: pct(
      targetEvents.length > 0 ? hitEvents.length / targetEvents.length : 0,
    ),
    totalPointsFromTargets,
    averagePointsPerHit:
      hitEvents.length > 0
        ? Math.round(totalPointsFromTargets / hitEvents.length)
        : 0,
    tenouchiHits: hitEvents.filter((event) => event.tenouchi).length,
    obstacleTotal: obstacleEvents.length,
    obstacleCleared: obstacleEvents.filter(
      (event) => event.result === "cleared",
    ).length,
    obstacleHit: obstacleEvents.filter((event) => event.result === "hit")
      .length,
    bestCuts: hitEvents
      .slice()
      .sort((a, b) => b.points - a.points)
      .slice(0, 5),
    weakestCuts: hitEvents
      .slice()
      .sort((a, b) => a.points - b.points)
      .slice(0, 5),
    recentMistakes,
    byDirection,
    byHand,
    bySize,
    bySpeed,
    byGrade,
    bestCategory,
    weakestCategory,
  };
}

export function formatRunShareText({
  mode,
  title,
  score,
  stats,
}: {
  mode: GameMode;
  title: string;
  score: number;
  stats: RunPerformanceStats;
}): string {
  const modeLabel = mode === "survival" ? "Survival" : title;
  return `Samurai Quest - ${modeLabel}: ${score.toLocaleString("en-US")} pts, ${stats.accuracyLabel} hits, ${stats.hitTargets}/${stats.totalTargets} targets, ${stats.obstacleCleared}/${stats.obstacleTotal} obstacles cleared.`;
}

export { GRADE_ORDER };
